import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, message, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { RoutingType } from '../types';
import { apiFetch } from '../api';

const RoutingList: React.FC = () => {
  const [routing, setRouting] = useState<RoutingType[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRouting, setEditingRouting] = useState<RoutingType | null>(null);
  const [brands, setBrands] = useState<string[]>([]);
  const [form] = Form.useForm();

  const fetchRouting = async () => {
    setLoading(true);
    try {
      const data = await apiFetch<RoutingType[]>('/routing/');
      setRouting(data);
    } catch (error: any) {
      message.error(error.message || 'Не удалось загрузить маршрутизацию');
    }
    setLoading(false);
  };

  const fetchBrands = async () => {
    try {
      const data = await apiFetch<any[]>('/brands/');
      setBrands(data.map((b) => b.name));
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchRouting();
    fetchBrands();
  }, []);

  const handleAdd = async (values: any) => {
    try {
      await apiFetch('/routing/', { method: 'POST', body: values });
      message.success('Маршрут добавлен');
      setModalVisible(false);
      fetchRouting();
      form.resetFields();
    } catch (error: any) {
      message.error(error.message || 'Не удалось добавить маршрут');
    }
  };

  const handleEdit = async (values: any) => {
    try {
      await apiFetch(`/routing/${editingRouting?.id}`, { method: 'PUT', body: values });
      message.success('Маршрут обновлён');
      setModalVisible(false);
      setEditingRouting(null);
      fetchRouting();
      form.resetFields();
    } catch (error: any) {
      message.error(error.message || 'Не удалось обновить маршрут');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiFetch(`/routing/${id}`, { method: 'DELETE' });
      message.success('Маршрут удалён');
      fetchRouting();
    } catch (error: any) {
      message.error(error.message || 'Не удалось удалить маршрут');
    }
  };

  const showEditModal = (routing: RoutingType) => {
    setEditingRouting(routing);
    form.setFieldsValue(routing);
    setModalVisible(true);
  };

  const columns = [
    { title: 'Бренд', dataIndex: 'brand', key: 'brand' },
    { title: 'Тип правила', dataIndex: 'rule_type', key: 'rule_type', render: (type: string) => {
      const types: Record<string, string> = {
        auto_reply: 'Автоответ',
        complaint: 'Жалоба',
        manager: 'Передать менеджеру',
        negative: 'Негатив',
        ignore: 'Игнорировать'
      };
      return types[type] || type;
    }},
    { title: 'Telegram Username', dataIndex: 'telegram_username', key: 'telegram_username' },
    { title: 'Telegram ID', dataIndex: 'telegram_user_id', key: 'telegram_user_id' },
    { title: 'Действия', key: 'actions', render: (_: any, record: RoutingType) => (
      <Space>
        <Button icon={<EditOutlined />} size="small" title="Редактировать" onClick={() => showEditModal(record)} />
        <Popconfirm title="Удалить ответственного?" okText="Удалить" cancelText="Отмена" okButtonProps={{ danger: true }} onConfirm={() => handleDelete(record.id)}>
          <Button icon={<DeleteOutlined />} size="small" danger title="Удалить" />
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingRouting(null); form.resetFields(); setModalVisible(true); }} style={{ marginBottom: 16 }}>
        Добавить правило маршрутизации
      </Button>
      <Table columns={columns} dataSource={routing} rowKey="id" loading={loading} scroll={{ x: 'max-content' }} />
      <Modal
        title={editingRouting ? "Редактировать правило" : "Новое правило маршрутизации"}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditingRouting(null); form.resetFields(); }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={editingRouting ? handleEdit : handleAdd}>
          <Form.Item name="brand" label="Бренд" rules={[{ required: true }]}>
            <Select options={brands.map((b) => ({ value: b }))} />
          </Form.Item>
          <Form.Item name="rule_type" label="Тип правила" rules={[{ required: true }]}>
            <Select options={[
              { value: 'auto_reply', label: 'Автоответ' },
              { value: 'complaint', label: 'Жалоба' },
              { value: 'manager', label: 'Передать менеджеру' },
              { value: 'negative', label: 'Негатив' },
              { value: 'ignore', label: 'Игнорировать' },
            ]} />
          </Form.Item>
          <Form.Item name="telegram_username" label="Telegram Username" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="telegram_user_id" label="Telegram ID" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">Сохранить</Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RoutingList;